// 全局 polyfill 初始化 - 必须在所有其他导入之前
import "./polyfill-init";

// Import polyfills first to ensure compatibility
import "./legacy/promise-with-resolvers";
import "./legacy/url-parse";
import { ensurePromiseWithResolvers, testPromiseWithResolvers } from "./utils/polyfill-test";

// 导入环境缓存控制工具
import { initCacheControl } from "./utils/envCache";

import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { createHashRouter, RouterProvider } from "react-router-dom";
import HistoryPage from "@/pages/HistoryPage";
import Knowledge from "@/pages/admin/knowledge/index";
import FileData from "@/pages/admin/fileData/index";
import UploadPdf from "@/pages/admin/fileData/uploadPdf";
import UploadMarkdown from "@/pages/admin/fileData/uploadMarkdown";
import UploadExcel from "@/pages/admin/fileData/uploadExcel";
import CheckEditor from "@/pages/admin/fileData/checkEditor";
import HistoricalIssues from "@/pages/admin/HistoricalIssues/index";
import TopicDetail from "@/pages/admin/topicDetail/index.tsx";
import TeamUser from "@/pages/admin/teamUser/index";
import Registration from "@/pages/Registration/index";
import ResetPassword from "@/pages/resetPassword/index";

import DetailPage from "@/pages/DetailPage/index.tsx";
import App from "./App.tsx";
import HomePage from "@/pages/HomePage.tsx";
import SpecialPage from "@/pages/special/index.tsx";
import { ThreadPage } from "@/pages/thread";
import { ThemeProvider } from "@/contexts/ThemeContext.tsx";
import { I18nProvider } from '@/locales';
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import ErrorTestPage from "@/pages/ErrorTestPage";
import PolyfillTestPage from "@/pages/PolyfillTestPage";
import LoginPage from "@/pages/LoginPage";
import SettingsPage from "@/pages/SettingsPage";
import NewChatPage from "@/pages/mobile/newChatPage";
import HkaiLoginPage from "@/pages/open/HkaiLoginPage";

// 强制确保 Promise.withResolvers 可用
console.log("[MAIN] Testing Promise.withResolvers before app initialization...");
if (!testPromiseWithResolvers()) {
  console.log("[MAIN] Promise.withResolvers not available, forcing injection...");
  ensurePromiseWithResolvers();
} else {
  console.log("[MAIN] Promise.withResolvers is already available");
}

// 初始化环境缓存控制
initCacheControl();
console.log("[MAIN] Environment cache control initialized");

// 检查当前环境并测试 polyfill
const isDevelopment = import.meta.env.MODE === "development";

// 强制检测和测试 Promise.withResolvers polyfill
(function () {
  console.log("🧪 Testing Promise.withResolvers polyfill...");

  // 检查 Promise.withResolvers 是否存在
  if (typeof Promise.withResolvers !== "function") {
    console.error("❌ CRITICAL: Promise.withResolvers is not available!");
    console.error("This will cause PDF preview and other features to fail.");

    // 尝试紧急修复
    if (!Promise.withResolvers) {
      console.log("🔧 Attempting emergency polyfill injection...");
      Promise.withResolvers = function <T = any>() {
        let resolve: (value: T | PromiseLike<T>) => void;
        let reject: (reason?: any) => void;

        const promise = new Promise<T>((res, rej) => {
          resolve = res;
          reject = rej;
        });

        return {
          promise,
          resolve: resolve!,
          reject: reject!,
        };
      };
      console.log("🔧 Emergency polyfill applied");
    }
  }

  // 功能测试
  try {
    const { promise, resolve } = Promise.withResolvers<string>();
    resolve("Polyfill working!");
    promise
      .then((result) => {
        console.log("✅ Promise.withResolvers test passed:", result);
      })
      .catch((error) => {
        console.error("❌ Promise.withResolvers test failed in promise:", error);
      });
  } catch (error) {
    console.error("❌ Promise.withResolvers test failed:", error);
    console.error("Browser info:", {
      userAgent: navigator.userAgent,
      promiseSupport: typeof Promise !== "undefined",
      withResolversSupport: typeof Promise.withResolvers,
    });
  }
})();

// 创建根元素
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

const root = ReactDOM.createRoot(rootElement);

const router = createHashRouter([
  {
    path: "/",
    element: <App />, // App 作为布局组件
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/",
        element: <HomePage />,
      },
      {
        path: "/:paramsId",
        element: <DetailPage />,
      },
      {
        path: "/history",
        element: <HistoryPage />,
      },
      {
        path: "/registration/:token",
        element: <Registration />,
      },
      {
        path: "/reset-password/:token",
        element: <ResetPassword />,
      },
      {
        path: "/admin/knowledge",
        element: <Knowledge />,
      },
      {
        path: "/admin/fileData",
        element: <FileData />,
      },
      {
        path: "/admin/fileDataEditor",
        element: <CheckEditor />,
      },
      // 上传文件页
      {
        path: "/admin/fileData/uploadPdf",
        element: <UploadPdf />,
      },
      {
        path: "/admin/fileData/uploadMarkdown",
        element: <UploadMarkdown />,
      },
      {
        path: "/admin/fileData/uploadExcel",
        element: <UploadExcel />,
      },
      {
        path: "/admin/HistoricalIssues",
        element: <HistoricalIssues />,
      },
      //历史对话详情
      {
        path: "/admin/topicDetail/:paramsId",
        element: <TopicDetail />,
      },
      {
        path: "/admin/teamUser",
        element: <TeamUser />,
      },
      {
        path: "/special",
        element: <SpecialPage />,
      },
      {
        path: "/thread/:threadId",
        element: <ThreadPage />,
      },
      { path: "/error-test", element: <ErrorTestPage /> },
      { path: "/polyfill-test", element: <PolyfillTestPage /> },
      // 移动端登录页面
      {
        path: "/login",
        element: <LoginPage />,
      },
      // 移动端设置页面
      {
        path: "/settings",
        element: <SettingsPage />,
      },
      // 移动端新建对话页面
      {
        path: "/mobile/newChat",
        element: <NewChatPage />,
      },
      // HKAI 第三方 JWT 登录
      {
        path: "/open/hkai",
        element: <HkaiLoginPage />,
      }
    ],
  },
]);

// 根据环境决定是否使用 Strict Mode
// 开发环境使用 Strict Mode
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <I18nProvider>
        <RouterProvider router={router} />
      </I18nProvider>
    </ThemeProvider>
  </React.StrictMode>
);

console.log("[APP INIT] Router context is present:", (window as any).__REACT_ROUTER_CONTEXT__ !== undefined);
